/** What the live mounts of one space are holding, read the only way there is: one socket each. */

import type { SceneDocument } from "@nebulize/sdk";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import type { StateEntity } from "@/lib/runtime";
import type { CanvasStreamOptions } from "@/lib/canvasStream";
import { useCanvasState } from "@/lib/useCanvasState";

import type { AddedActivityRow } from "../../types";

/** One mount's reading. `error` and `waiting` are states, never values to draw over. */
export interface MountReading {
  mountId: string;
  /** The customer's name for the added activity this mount is running. */
  name: string;
  activityId: string;
  scenes: readonly SceneDocument[];
  entities: readonly StateEntity[];
  error: string | null;
  /** Place in the admission queue, or null once admitted. Not an error: the socket is open. */
  waiting: number | null;
  /** Whether anything has arrived yet. A blank read is not an empty mount. */
  read: boolean;
}

function MountReader({
  row,
  asCanvas,
  stream,
  onReading,
}: {
  readonly row: AddedActivityRow;
  /** Read through one declared canvas, the way the stage's owner preview does. Ignored for
   * anyone but the space's owner, so a moderator's read degrades to whatever it would see. */
  readonly asCanvas: string | undefined;
  /** A test seam for an injected socket factory, passed through to `useCanvasState`. A real
   * page never passes it. */
  readonly stream: CanvasStreamOptions | undefined;
  readonly onReading: (mountId: string, reading: MountReading) => void;
}) {
  const mountId = row.mount?.mount_id ?? "";
  const { state, error, waitingPosition } = useCanvasState(mountId, stream, asCanvas);

  const reading = useMemo<MountReading>(
    () => ({
      mountId,
      name: row.name,
      activityId: row.activity_id,
      scenes: state?.scenes ?? [],
      entities: state?.entities ?? [],
      error,
      waiting: waitingPosition,
      read: state !== null,
    }),
    [mountId, row.name, row.activity_id, state, error, waitingPosition],
  );

  useEffect(() => {
    onReading(mountId, reading);
  }, [onReading, mountId, reading]);

  return null;
}

/** Every live mount of the given rows, each reporting what it holds. */
export function LiveMounts({
  rows,
  asCanvas,
  stream,
  children,
}: {
  readonly rows: readonly AddedActivityRow[];
  /** The canvas kind to preview through, the same value the stage is currently drawing.
   * Undefined reads through no canvas at all, the socket's ordinary participant grant. */
  readonly asCanvas?: string;
  /** A test seam for an injected socket factory. A real page never passes it. */
  readonly stream?: CanvasStreamOptions;
  readonly children: (readings: readonly MountReading[]) => ReactNode;
}) {
  const [readings, setReadings] = useState<Record<string, MountReading>>({});

  const report = useCallback((mountId: string, reading: MountReading) => {
    setReadings((held) => (held[mountId] === reading ? held : { ...held, [mountId]: reading }));
  }, []);

  const live = useMemo(() => rows.filter((row) => row.mount?.mount_id != null), [rows]);

  const list = useMemo(
    () =>
      live
        .map((row) => readings[row.mount?.mount_id ?? ""])
        .filter((one): one is MountReading => one !== undefined),
    [live, readings],
  );

  return (
    <>
      {live.map((row) => (
        <MountReader
          key={row.mount?.mount_id ?? row.added_activity_id}
          row={row}
          asCanvas={asCanvas}
          stream={stream}
          onReading={report}
        />
      ))}
      {children(list)}
    </>
  );
}
